import {
  type Customer,
  customerKindLabel,
  type DocumentListQuery,
  type DocumentSummaryRow,
  labelOf,
  PAYMENT_METHODS,
  type Paginated,
  type Payment,
  partyIdentificationLabel,
  QUOTE_STATUS_LABEL,
  type QuoteSummaryRow,
} from '@facture/contracts'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ArrowLeftIcon,
  CalendarClockIcon,
  MailIcon,
  MapPinIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  ReceiptIcon,
  SendIcon,
  Trash2Icon,
  UserRoundIcon,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { confirm } from '@/components/ui/confirm'
import { useDebounced } from '@/components/ui/data-table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ClearFilters, FilterBar, FilterSearch, FilterSelect } from '@/components/ui/filter-bar'
import { Avatar, Tabs, TabsList, TabsTrigger } from '@/components/ui/misc'
import { Page } from '@/components/ui/page'
import { Skeleton } from '@/components/ui/skeleton'
import { customerQuery, useSetCustomerActive } from '@/features/customers/api'
import { CustomerFormSheet } from '@/features/customers/customer-form-sheet'
import {
  CustomerOverview,
  customerInsightsQuery,
  paymentProfile,
} from '@/features/customers/customer-overview'
import { CustomerSpecialPrices } from '@/features/customers/customer-special-prices'
import { documentsQuery } from '@/features/documents/api'
import { HaciendaStatusIcon, PaymentBadge } from '@/features/documents/status'
import { STATUS_VARIANT } from '@/features/quotes/quote-editor'
import { api, errorMessage } from '@/lib/api'
import { formatDate, formatIdentification, formatMoney } from '@/lib/format'
import { useCan } from '@/lib/session'

const TABS = [
  ['overview', 'Resumen'],
  ['info', 'Información'],
  ['invoices', 'Facturas'],
  ['tickets', 'Tiquetes'],
  ['notes', 'Notas'],
  ['quotes', 'Cotizaciones'],
  ['payments', 'Cobros'],
  ['prices', 'Precios especiales'],
] as const

export const Route = createFileRoute('/_app/customers_/$id')({
  staticData: { crumb: 'Cliente' },
  validateSearch: z.object({
    tab: z
      .enum(['overview', 'info', 'invoices', 'tickets', 'notes', 'quotes', 'payments', 'prices'])
      .optional(),
  }),
  component: CustomerDetailPage,
})

type Money = 'CRC' | 'USD' | 'EUR'

function CustomerDetailPage() {
  const { id } = Route.useParams()
  const { tab = 'overview' } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCan('customers:write')
  const canPay = useCan('payments:write')
  const canIssue = useCan('documents:issue')
  const setActive = useSetCustomerActive()
  const { data: customer, isLoading } = useQuery(customerQuery(id))
  const { data: insights } = useQuery(customerInsightsQuery(id))
  const profile = paymentProfile(insights?.totals)
  const [editing, setEditing] = useState(false)
  const statement = useMutation({
    mutationFn: () => api.post<{ to: string[]; invoices: number }>(`/customers/${id}/statement`),
    onSuccess: (r) => toast.success(`Estado de cuenta enviado a ${r.to.join(', ')}`),
  })

  if (isLoading || !customer) {
    return (
      <Page>
        <Skeleton className="mb-6 h-20 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </Page>
    )
  }

  const hasEmail = !!customer.email || customer.contacts.some((c) => c.email)

  return (
    <Page>
      <div className="relative mb-6 overflow-hidden rounded-2xl border border-border bg-card shadow-elevation-1">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/12 via-primary/4 to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-16 size-72 rounded-full bg-primary/10 blur-3xl"
        />
        <div className="relative flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <Button asChild variant="ghost" size="icon-sm" className="mt-1">
              <Link to="/customers" aria-label="Volver">
                <ArrowLeftIcon />
              </Link>
            </Button>
            <Avatar name={customer.name} className="size-16 text-lg ring-4 ring-card" />
            <div className="min-w-0">
              <h1 className="truncate font-semibold text-2xl tracking-tight">{customer.name}</h1>
              {customer.tradeName && customer.tradeName !== customer.name && (
                <div className="truncate text-muted-foreground text-sm">{customer.tradeName}</div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="primary">{customerKindLabel[customer.kind]}</Badge>
                {profile && <Badge variant={profile.variant}>{profile.label}</Badge>}
                {!customer.isActive && <Badge>Archivado</Badge>}
                {customer.identification && (
                  <span className="font-mono text-muted-foreground text-xs">
                    {customer.identificationType
                      ? `${partyIdentificationLabel[customer.identificationType]} · `
                      : ''}
                    {formatIdentification(customer.identificationType ?? '', customer.identification)}
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-muted-foreground text-sm">
                {customer.email && (
                  <span className="flex items-center gap-1.5">
                    <MailIcon className="size-3.5" /> {customer.email}
                  </span>
                )}
                {customer.phone && (
                  <span className="flex items-center gap-1.5">
                    <PhoneIcon className="size-3.5" /> {customer.phone}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <CalendarClockIcon className="size-3.5" />
                  {customer.creditDays ? `Crédito a ${customer.creditDays} días` : 'Contado'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-xl border border-border bg-subtle px-4 py-2 text-right">
              <div className="text-muted-foreground text-xs">Saldo por cobrar</div>
              <div className={`font-semibold text-lg tabular ${customer.balance > 0 ? 'text-primary' : ''}`}>
                {formatMoney(customer.balance)}
              </div>
            </div>
            {customer.balance > 0 && canPay && (
              <Button
                variant="outline"
                loading={statement.isPending}
                disabled={!hasEmail}
                onClick={() => statement.mutate()}
              >
                <SendIcon /> Estado de cuenta
              </Button>
            )}
            {canIssue && customer.isActive && (
              <>
                <Button asChild variant="outline">
                  <Link to="/documents/new" search={{ kind: 'TE', customerId: id }}>
                    <ReceiptIcon /> Nuevo tiquete
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/documents/new" search={{ kind: 'FE', customerId: id }}>
                    <PlusIcon /> Nueva factura
                  </Link>
                </Button>
              </>
            )}
            {canWrite && (
              <>
                <Button onClick={() => setEditing(true)}>
                  <PencilIcon /> Editar
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label="Más acciones">
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() =>
                        customer.isActive
                          ? confirm({
                              title: '¿Archivar este contacto?',
                              description:
                                'No aparecerá al crear comprobantes nuevos; su historial se conserva.',
                              confirmLabel: 'Archivar',
                              onConfirm: () =>
                                setActive.mutateAsync({ id, isActive: false }).then(() => {
                                  toast.success('Contacto archivado')
                                }),
                            })
                          : setActive.mutate(
                              { id, isActive: true },
                              { onSuccess: () => toast.success('Contacto restaurado') },
                            )
                      }
                    >
                      {customer.isActive ? <ArchiveIcon /> : <ArchiveRestoreIcon />}
                      {customer.isActive ? 'Archivar' : 'Restaurar'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      destructive
                      onSelect={() =>
                        confirm({
                          title: '¿Eliminar este contacto?',
                          description:
                            'Solo se puede eliminar si no tiene comprobantes; si los tiene, archivalo.',
                          confirmLabel: 'Eliminar',
                          destructive: true,
                          onConfirm: async () => {
                            try {
                              await api.delete(`/customers/${id}`)
                              toast.success('Contacto eliminado')
                              navigate({ to: '/customers' })
                            } catch (error) {
                              toast.error(
                                error instanceof Error && 'code' in error && error.code === 'in_use'
                                  ? 'Tiene comprobantes: archivalo en lugar de eliminarlo'
                                  : errorMessage(error),
                              )
                            }
                          },
                        })
                      }
                    >
                      <Trash2Icon /> Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => navigate({ search: { tab: v as typeof tab }, replace: true })}
        className="mb-6 min-w-0 max-w-full"
      >
        <TabsList>
          {TABS.map(([value, label]) => (
            <TabsTrigger key={value} value={value}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === 'overview' && <CustomerOverview customerId={id} />}
      {tab === 'info' && <InfoTab customer={customer} />}
      {tab === 'invoices' && (
        <DocumentsTab customerId={id} kind="FE" empty="Este cliente aún no tiene facturas." />
      )}
      {tab === 'tickets' && (
        <DocumentsTab customerId={id} kind="TE" empty="Este cliente aún no tiene tiquetes." />
      )}
      {tab === 'notes' && (
        <DocumentsTab
          customerId={id}
          kind="NOTES"
          empty="Este cliente aún no tiene notas de crédito o débito."
        />
      )}
      {tab === 'quotes' && <QuotesTab customerId={id} />}
      {tab === 'payments' && <PaymentsTab customerId={id} />}
      {tab === 'prices' && <CustomerSpecialPrices customerId={id} />}

      <CustomerFormSheet open={editing} onOpenChange={setEditing} customer={customer} />
    </Page>
  )
}

/* ------------------------------ información ------------------------------ */

function InfoTab({ customer }: { customer: Customer }) {
  const rows: [string, ReactNode][] = [
    ['Nombre comercial', customer.tradeName],
    [
      'Actividad económica',
      customer.economicActivity && <span className="font-mono">{customer.economicActivity}</span>,
    ],
    ['Crédito', customer.creditDays ? `${customer.creditDays} días` : 'Contado'],
    ['Cliente desde', formatDate(customer.createdAt)],
  ]
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Datos del cliente</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm">
          {rows
            .filter(([, v]) => v)
            .map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-4">
                <span className="text-muted-foreground">{label}</span>
                <span className="text-right font-medium">{value}</span>
              </div>
            ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contacto y ubicación</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2.5 text-sm">
          {customer.email && <Line icon={MailIcon}>{customer.email}</Line>}
          {customer.ccEmails.map((e) => (
            <Line key={e} icon={MailIcon} muted>
              {e} · copia
            </Line>
          ))}
          {customer.phone && <Line icon={PhoneIcon}>{customer.phone}</Line>}
          {(customer.addressDetail || customer.provinceCode) && (
            <Line icon={MapPinIcon}>
              {[
                customer.addressDetail,
                customer.provinceCode &&
                  `${customer.provinceCode}-${customer.cantonCode}-${customer.districtCode}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Line>
          )}
          {!customer.email && !customer.phone && !customer.addressDetail && (
            <p className="text-muted-foreground">Sin datos de contacto.</p>
          )}
        </CardContent>
      </Card>

      {customer.contacts.length > 0 && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Personas de contacto</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {customer.contacts.map((c) => (
              <div key={c.id} className="flex items-start gap-3 rounded-lg border border-border p-3">
                <UserRoundIcon className="mt-0.5 size-4 text-muted-foreground" />
                <div className="min-w-0 flex-1 text-sm">
                  <div className="font-medium">
                    {c.name}
                    {c.position && <span className="font-normal text-muted-foreground"> · {c.position}</span>}
                  </div>
                  <div className="truncate text-muted-foreground text-xs">
                    {[c.email, c.phone].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {c.receivesDocuments && c.email && <Badge variant="success">Recibe copia</Badge>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {customer.notes && (
        <div className="rounded-xl bg-warning-soft px-4 py-3 text-sm text-warning-soft-foreground lg:col-span-2">
          {customer.notes}
        </div>
      )}
    </div>
  )
}

function Line({
  icon: Icon,
  muted,
  children,
}: {
  icon: typeof MailIcon
  muted?: boolean
  children: ReactNode
}) {
  return (
    <div className={`flex items-center gap-2.5 ${muted ? 'text-muted-foreground' : ''}`}>
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 truncate">{children}</span>
    </div>
  )
}

/* ------------------------------ listas ------------------------------ */

function TableCard({
  children,
  empty,
  isEmpty,
  loading,
}: {
  children: ReactNode
  empty: string
  isEmpty: boolean
  loading: boolean
}) {
  if (loading) return <Skeleton className="h-48 rounded-xl" />
  return (
    <Card className="overflow-hidden">
      {isEmpty ? (
        <p className="px-6 py-14 text-center text-muted-foreground text-sm">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-sm">{children}</table>
        </div>
      )}
    </Card>
  )
}

const th = 'h-10 whitespace-nowrap px-4 text-left font-medium text-muted-foreground text-xs'
const td = 'h-12 whitespace-nowrap px-4'

function DocumentsTab({
  customerId,
  kind,
  empty,
}: {
  customerId: string
  kind: 'FE' | 'TE' | 'NOTES'
  empty: string
}) {
  const navigate = useNavigate()
  const [text, setText] = useState('')
  const q = useDebounced(text)
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)
  const list = useQuery(
    documentsQuery({
      kind,
      customerId,
      search: q || undefined,
      status: ['accepted', 'rejected'].includes(status) ? status : undefined,
      payment: ['paid', 'unpaid'].includes(status) ? status : undefined,
      page,
      pageSize: 25,
    } as DocumentListQuery),
  )
  const rows = list.data?.items ?? []
  const pages = Math.max(1, Math.ceil((list.data?.total ?? 0) / 25))
  return (
    <div className="grid gap-3">
      <FilterBar>
        <FilterSearch
          value={text}
          onChange={(v) => {
            setText(v)
            setPage(1)
          }}
          placeholder="Buscar por número o clave…"
        />
        {kind === 'FE' && (
          <FilterSelect
            label="Estado"
            value={status}
            onValueChange={(v) => {
              setStatus(v)
              setPage(1)
            }}
            options={[
              { value: 'all', label: 'Todas' },
              { value: 'accepted', label: 'Aceptadas' },
              { value: 'rejected', label: 'Rechazadas' },
              { value: 'paid', label: 'Pagadas' },
              { value: 'unpaid', label: 'No pagadas' },
            ]}
          />
        )}
        <ClearFilters
          active={!!text || status !== 'all'}
          onClear={() => {
            setText('')
            setStatus('all')
            setPage(1)
          }}
        />
      </FilterBar>
      <TableCard empty={empty} isEmpty={!rows.length} loading={list.isLoading}>
        <thead className="bg-subtle">
          <tr>
            <th className={th}>Consecutivo</th>
            {kind === 'NOTES' && <th className={th}>Tipo</th>}
            <th className={th}>Fecha</th>
            {kind === 'FE' && <th className={th}>Vence</th>}
            <th className={`${th} text-right`}>Total</th>
            {kind === 'FE' && <th className={`${th} text-right`}>Saldo</th>}
            <th className={th}>Hacienda</th>
            {kind === 'FE' && <th className={th}>Cobro</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((d: DocumentSummaryRow) => {
            const overdue =
              !!d.dueDate && d.balance > 0.005 && d.dueDate < new Date().toISOString().slice(0, 10)
            const days = overdue
              ? Math.floor((Date.now() - new Date(`${d.dueDate}T00:00:00-06:00`).getTime()) / 86_400_000)
              : 0
            return (
              <tr
                key={d.id}
                className="cursor-pointer border-border border-t transition-colors hover:bg-accent/40"
                onClick={() => navigate({ to: '/documents/$id', params: { id: d.id } })}
              >
                <td className={`${td} font-mono text-xs`}>{d.consecutive ?? 'Borrador'}</td>
                {kind === 'NOTES' && <td className={td}>{d.kind}</td>}
                <td className={td}>{d.issuedAt ? formatDate(d.issuedAt) : '—'}</td>
                {kind === 'FE' && (
                  <td className={td}>
                    {d.dueDate ? formatDate(d.dueDate) : '—'}
                    {overdue && (
                      <span className="ml-2 text-destructive text-xs">
                        Vencida hace {days} {days === 1 ? 'día' : 'días'}
                      </span>
                    )}
                  </td>
                )}
                <td className={`${td} text-right tabular`}>{formatMoney(d.total, d.currency as Money)}</td>
                {kind === 'FE' && (
                  <td
                    className={`${td} text-right tabular ${d.balance > 0.005 ? 'font-medium text-primary' : 'text-muted-foreground'}`}
                  >
                    {formatMoney(d.balance, d.currency as Money)}
                  </td>
                )}
                <td className={td}>
                  <HaciendaStatusIcon status={d.haciendaStatus} />
                </td>
                {kind === 'FE' && (
                  <td className={td}>
                    <PaymentBadge status={d.paymentStatus} overdue={overdue} />
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </TableCard>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-muted-foreground text-sm">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Anterior
          </Button>
          <span className="tabular">
            {page} / {pages}
          </span>
          <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            Siguiente
          </Button>
        </div>
      )}
    </div>
  )
}

function QuotesTab({ customerId }: { customerId: string }) {
  const navigate = useNavigate()
  const list = useQuery({
    queryKey: ['quotes', 'list', { customerId, kind: 'quote' }],
    queryFn: () =>
      api.get<Paginated<QuoteSummaryRow>>('/quotes', { query: { kind: 'quote', customerId, pageSize: 50 } }),
  })
  const rows = list.data?.items ?? []
  return (
    <TableCard
      empty="Este cliente aún no tiene cotizaciones."
      isEmpty={!rows.length}
      loading={list.isLoading}
    >
      <thead className="bg-subtle">
        <tr>
          <th className={th}>Número</th>
          <th className={th}>Título</th>
          <th className={th}>Válida hasta</th>
          <th className={`${th} text-right`}>Total</th>
          <th className={th}>Estado</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((q) => (
          <tr
            key={q.id}
            className="cursor-pointer border-border border-t transition-colors hover:bg-accent/40"
            onClick={() => navigate({ to: '/quotes/$id', params: { id: q.id } })}
          >
            <td className={`${td} font-mono text-xs`}>COT-{q.number}</td>
            <td className={td}>{q.title ?? '—'}</td>
            <td className={td}>{q.validUntil ? formatDate(q.validUntil) : '—'}</td>
            <td className={`${td} text-right tabular`}>{formatMoney(q.total, q.currency as Money)}</td>
            <td className={td}>
              <Badge variant={STATUS_VARIANT[q.status]}>{QUOTE_STATUS_LABEL[q.status]}</Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  )
}

function PaymentsTab({ customerId }: { customerId: string }) {
  const navigate = useNavigate()
  const list = useQuery({
    queryKey: ['payments', 'list', { customerId }],
    queryFn: () => api.get<Paginated<Payment>>('/payments', { query: { customerId, pageSize: 50 } }),
  })
  const rows = list.data?.items ?? []
  return (
    <TableCard
      empty="Este cliente aún no tiene cobros registrados."
      isEmpty={!rows.length}
      loading={list.isLoading}
    >
      <thead className="bg-subtle">
        <tr>
          <th className={th}>Fecha</th>
          <th className={th}>Factura</th>
          <th className={th}>Medio</th>
          <th className={th}>Referencia</th>
          <th className={`${th} text-right`}>Monto</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr
            key={p.id}
            className={`cursor-pointer border-border border-t transition-colors hover:bg-accent/40 ${p.voidedAt ? 'text-muted-foreground line-through' : ''}`}
            onClick={() => navigate({ to: '/documents/$id', params: { id: p.documentId } })}
          >
            <td className={td}>{formatDate(p.paidAt)}</td>
            <td className={`${td} font-mono text-xs`}>{p.documentConsecutive ?? '—'}</td>
            <td className={td}>{labelOf(PAYMENT_METHODS, p.method)}</td>
            <td className={td}>{p.reference ?? '—'}</td>
            <td className={`${td} text-right tabular`}>{formatMoney(p.amount, p.currency as Money)}</td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  )
}
